/**
 * Small time-series charts (plain SVG, no chart library).
 *
 * Follows the dataviz method: one measure per chart and one y-axis (small
 * multiples instead of dual axes); categorical slots 1–2 (#2a78d6 blue,
 * #eb6834 orange) and status-critical (#d03b3b) for errors, validated against
 * the white panel; 2px lines, ≤24px columns with 4px rounded data-ends and a
 * 2px surface gap between stacked segments; recessive hairline grid; legend for
 * ≥2 series; hover tooltip on every chart; a table view for every chart.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

export const SERIES = { blue: '#2a78d6', orange: '#eb6834', critical: '#d03b3b' } as const;

export type Point = { t: string; values: Record<string, number | null> };
export type SeriesDef = { key: string; label: string; color: string };

const H = 150;
const PAD = { top: 10, right: 12, bottom: 22, left: 44 };

const useWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(600);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(240, Math.floor(e.contentRect.width))));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return { ref, w };
};

/** 3–4 round ticks from 0 to ≥ max. */
const ticks = (max: number): number[] => {
  if (max <= 0) return [0, 1];
  const raw = max / 3;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.001; v += step) out.push(v);
  if (out[out.length - 1] < max) out.push(out[out.length - 1] + step);
  return out;
};

const timeLabel = (iso: string, bucket: 'hour' | 'day') =>
  bucket === 'hour'
    ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const Legend = ({ series }: { series: SeriesDef[] }) =>
  series.length < 2 ? null : (
    <div className="flex flex-wrap gap-3 text-xs text-ink-2">
      {series.map((s) => (
        <span key={s.key} className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2 w-3 rounded-sm" style={{ background: s.color }} />
          {s.label}
        </span>
      ))}
    </div>
  );

type ChartProps = {
  title: string;
  data: Point[];
  series: SeriesDef[];
  bucket: 'hour' | 'day';
  format: (v: number) => string;
  kind: 'columns' | 'lines';
  empty?: string;
};

export const TimeChart = ({ title, data, series, bucket, format, kind, empty }: ChartProps) => {
  const { ref, w } = useWidth();
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const innerW = w - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const n = data.length;
  const slot = n ? innerW / n : innerW;

  const max = useMemo(() => {
    const vals = data.map((d) =>
      kind === 'columns' ? series.reduce((a, s) => a + (d.values[s.key] ?? 0), 0) : Math.max(0, ...series.map((s) => d.values[s.key] ?? 0)),
    );
    return Math.max(0, ...vals);
  }, [data, series, kind]);
  const yt = ticks(max);
  const top = yt[yt.length - 1] || 1;
  const y = (v: number) => PAD.top + innerH - (v / top) * innerH;
  const xc = (i: number) => PAD.left + slot * i + slot / 2;
  const hasData = data.some((d) => series.some((s) => (d.values[s.key] ?? 0) > 0));
  const labelIdx = n <= 1 ? [0] : [0, Math.floor((n - 1) / 2), n - 1];

  return (
    <section className="rounded-lg border border-line bg-panel">
      <header className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3">
        <h3 className="text-[13px] font-semibold">{title}</h3>
        <div className="flex items-center gap-3">
          <Legend series={series} />
          <button className="text-xs text-ink-3 hover:text-ink" onClick={() => setAsTable((v) => !v)}>
            {asTable ? 'Chart' : 'Table'}
          </button>
        </div>
      </header>
      <div ref={ref} className="relative px-1 pb-2">
        {!hasData ? (
          <p className="flex h-[150px] items-center justify-center text-[13px] text-ink-3">{empty ?? 'No data in this period'}</p>
        ) : asTable ? (
          <div className="max-h-[150px] overflow-auto px-3">
            <table className="w-full text-xs tabular-nums">
              <thead>
                <tr className="text-left text-ink-3">
                  <th className="py-1 font-medium">{bucket === 'hour' ? 'Hour' : 'Day'}</th>
                  {series.map((s) => (
                    <th key={s.key} className="py-1 text-right font-medium">
                      {s.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data
                  .filter((d) => series.some((s) => d.values[s.key] !== null && d.values[s.key] !== 0))
                  .map((d) => (
                    <tr key={d.t} className="border-t border-line-2">
                      <td className="py-1">{timeLabel(d.t, bucket)}</td>
                      {series.map((s) => (
                        <td key={s.key} className="py-1 text-right">
                          {d.values[s.key] === null ? '—' : format(d.values[s.key]!)}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
            <svg width={w} height={H} role="img" aria-label={title} onMouseLeave={() => setHover(null)}>
              {yt.map((v) => (
                <g key={v}>
                  <line x1={PAD.left} x2={w - PAD.right} y1={y(v)} y2={y(v)} stroke="var(--color-line)" strokeWidth={1} />
                  <text x={PAD.left - 6} y={y(v)} dy="0.32em" textAnchor="end" fontSize={10} fill="var(--color-ink-3)">
                    {format(v)}
                  </text>
                </g>
              ))}
              {labelIdx.map((i) => (
                <text key={i} x={xc(i)} y={H - 6} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} fontSize={10} fill="var(--color-ink-3)">
                  {data[i] ? timeLabel(data[i].t, bucket) : ''}
                </text>
              ))}

              {kind === 'columns'
                ? data.map((d, i) => {
                    const bw = Math.max(2, Math.min(24, slot - 2));
                    let base = 0;
                    const segs = series
                      .map((s) => ({ s, v: d.values[s.key] ?? 0 }))
                      .filter((x) => x.v > 0);
                    return (
                      <g key={d.t} opacity={hover === null || hover === i ? 1 : 0.55}>
                        {segs.map(({ s, v }, j) => {
                          const y0 = y(base);
                          const y1 = y(base + v);
                          base += v;
                          const isTop = j === segs.length - 1;
                          const gap = j > 0 ? 2 : 0; // surface gap between stacked segments
                          const hgt = Math.max(0, y0 - y1 - gap);
                          const x = xc(i) - bw / 2;
                          const r = isTop ? Math.min(4, hgt, bw / 2) : 0;
                          const yTop = y1;
                          return (
                            <path
                              key={s.key}
                              fill={s.color}
                              d={`M${x},${yTop + hgt} V${yTop + r} Q${x},${yTop} ${x + r},${yTop} H${x + bw - r} Q${x + bw},${yTop} ${x + bw},${yTop + r} V${yTop + hgt} Z`}
                            />
                          );
                        })}
                      </g>
                    );
                  })
                : series.map((s) => {
                    let dStr = '';
                    let pen = false;
                    data.forEach((p, i) => {
                      const v = p.values[s.key];
                      if (v === null || v === undefined) {
                        pen = false;
                        return;
                      }
                      dStr += `${pen ? 'L' : 'M'}${xc(i)},${y(v)} `;
                      pen = true;
                    });
                    return <path key={s.key} d={dStr} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />;
                  })}

              {hover !== null && kind === 'lines' && (
                <g>
                  <line x1={xc(hover)} x2={xc(hover)} y1={PAD.top} y2={PAD.top + innerH} stroke="var(--color-ink-3)" strokeWidth={1} />
                  {series.map((s) => {
                    const v = data[hover].values[s.key];
                    return v === null || v === undefined ? null : <circle key={s.key} cx={xc(hover)} cy={y(v)} r={4} fill={s.color} stroke="var(--color-panel)" strokeWidth={2} />;
                  })}
                </g>
              )}

              {/* hit targets: the whole slot, bigger than the mark */}
              {data.map((d, i) => (
                <rect key={d.t} x={PAD.left + slot * i} y={PAD.top} width={slot} height={innerH} fill="transparent" onMouseEnter={() => setHover(i)} />
              ))}
            </svg>
            {hover !== null && (
              <div
                className="pointer-events-none absolute top-1 z-10 min-w-36 rounded-md border border-line bg-panel px-2.5 py-1.5 text-xs shadow-md"
                style={xc(hover) > w / 2 ? { right: w - xc(hover) + 10 } : { left: xc(hover) + 10 }}
              >
                <div className="mb-1 font-medium">{timeLabel(data[hover].t, bucket)}</div>
                {series.map((s) => (
                  <div key={s.key} className="flex items-center justify-between gap-3 tabular-nums">
                    <span className="inline-flex items-center gap-1.5 text-ink-2">
                      <span className="inline-block size-2 rounded-sm" style={{ background: s.color }} />
                      {s.label}
                    </span>
                    <span>{data[hover].values[s.key] === null ? '—' : format(data[hover].values[s.key] ?? 0)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export type StatsRow = { bucket: string; requests: number; errors: number; costUsd: number | null; inputTokens: number; outputTokens: number; p50Ms: number | null; p95Ms: number | null };

/** Every bucket in the window, so gaps read as zero traffic rather than being skipped. */
export const fillBuckets = (rows: StatsRow[], hours: number, bucket: 'hour' | 'day'): Array<StatsRow & { empty: boolean }> => {
  const step = bucket === 'hour' ? 3600_000 : 86400_000;
  const now = new Date();
  const end = bucket === 'hour' ? Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours()) : Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const count = bucket === 'hour' ? hours : Math.ceil(hours / 24);
  const byKey = new Map(rows.map((r) => [Date.parse(r.bucket), r]));
  const out: Array<StatsRow & { empty: boolean }> = [];
  for (let i = count - 1; i >= 0; i--) {
    const t = end - i * step;
    const r = byKey.get(t);
    out.push(r ? { ...r, empty: false } : { bucket: new Date(t).toISOString(), requests: 0, errors: 0, costUsd: null, inputTokens: 0, outputTokens: 0, p50Ms: null, p95Ms: null, empty: true });
  }
  return out;
};

const compact = (v: number) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(v >= 10_000 ? 0 : 1)}k` : `${Math.round(v)}`);
const ms = (v: number) => (v === 0 ? '0' : v >= 1000 ? `${Number((v / 1000).toFixed(v >= 10_000 ? 0 : 1))}s` : `${Math.round(v)}ms`);
const usd = (v: number) => (v === 0 ? '$0' : v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`);

/** The four small multiples: traffic, latency, cost, tokens. */
export const UsageCharts = ({ rows, hours, bucket, className }: { rows: StatsRow[]; hours: number; bucket: 'hour' | 'day'; className?: string }) => {
  const filled = fillBuckets(rows, hours, bucket);
  const pts = (f: (r: StatsRow) => Record<string, number | null>): Point[] => filled.map((r) => ({ t: r.bucket, values: f(r) }));
  const anyCost = rows.some((r) => r.costUsd !== null);
  return (
    <div className={cn('grid gap-4 lg:grid-cols-2', className)}>
      <TimeChart
        title="Requests"
        kind="columns"
        bucket={bucket}
        format={compact}
        series={[
          { key: 'ok', label: 'Succeeded', color: SERIES.blue },
          { key: 'errors', label: 'Failed', color: SERIES.critical },
        ]}
        data={pts((r) => ({ ok: r.requests - r.errors, errors: r.errors }))}
      />
      <TimeChart
        title="Latency"
        kind="lines"
        bucket={bucket}
        format={ms}
        series={[
          { key: 'p50', label: 'p50', color: SERIES.blue },
          { key: 'p95', label: 'p95', color: SERIES.orange },
        ]}
        data={pts((r) => ({ p50: r.p50Ms, p95: r.p95Ms }))}
      />
      <TimeChart
        title="Cost (USD)"
        kind="columns"
        bucket={bucket}
        format={usd}
        series={[{ key: 'cost', label: 'Cost', color: SERIES.blue }]}
        data={pts((r) => ({ cost: r.costUsd }))}
        empty={anyCost || !rows.length ? undefined : 'No prices set for the models used — add them under Models'}
      />
      <TimeChart
        title="Tokens"
        kind="columns"
        bucket={bucket}
        format={compact}
        series={[
          { key: 'input', label: 'Input', color: SERIES.blue },
          { key: 'output', label: 'Output', color: SERIES.orange },
        ]}
        data={pts((r) => ({ input: r.inputTokens, output: r.outputTokens }))}
      />
    </div>
  );
};
